import axios, { AxiosRequestConfig } from "axios";
import { isViewMatched } from "brew-js-react";
import { combinePath } from "brew-js/util/path";
import { cookie } from "brew-js/util/common";
import { bind } from "zeta-dom/domUtil";
import { ZetaEventContainer } from "zeta-dom/events";
import { always, combineFn, errorWithCode, isErrorWithCode, resolveAll, setIntervalSafe } from "zeta-dom/util";
import api from "src/api";
import { UserInfo } from "src/api/types";
import config from "src/config";
import { API_HOST, CLASSIC_EAC_URL, IHS_HOST } from "src/config/env";
import { ErrorCode, PasswordStatus } from "src/core/constants";
import { assign, parseDuration, parseJWT } from "src/util/common";
import { ResetPassword } from "src/views";
import { onServiceWorkerEvent } from "./serviceWorker";
import Bus from 'src/util/eventBus';
import { removeErrorApi } from "src/api/implementation/helper";
import { sendAnalytic } from "./analytics";

const STORAGE_KEY_TOKEN = 'eac.token';
const STORAGE_KEY_REDIRECT = 'eac.noredirect';
const STORAGE_KEY_INTERACT = 'eac.interact';
const PRESERVED_COOKIE_NAMES = new Set([STORAGE_KEY_TOKEN, 'deviceId']);

const axiosClient = axios.create();
const emitter = new ZetaEventContainer<AuthContext, AuthContextEventMap>();

let authHeader: { authorization: string, 'x-AIAHK-Agent-ID'?: string } | undefined;
let newAuthHeader: { authorization: string } | undefined;
export let ssoStatus: boolean;

axiosClient.interceptors.request.use(async (config1) => {
    // 检查是否是 gateway API 请求
    const url = new URL(config1.url!, config1.baseURL);
    if (authHeader && (url.origin === API_HOST || config1.url?.includes("checkFRStatus") || config1.url?.includes("refreshFRStatus"))) {
        config1 = { ...config1 };
        // 检查是否是 checkFRStatus 请求
        if (config1.url?.includes("checkFRStatus") || config1.url?.includes("refreshFRStatus")) {
            config1.headers = { ...config1.headers, ...newAuthHeader };
        } else {
            // 其他请求正常添加授权头
            config1.headers = { ...config1.headers, ...authHeader };
        }
    }
    return config1;
});

onServiceWorkerEvent('getAuthorizationHeader', e => {
    e.handled(authHeader);
});

export const authAdapter = (config1: AxiosRequestConfig) => {
    return axiosClient({ ...config1, adapter: undefined });
};

export interface UserContext extends Readonly<UserInfo> {
    readonly canViewMyPerformancePage: boolean;
    readonly canViewCampaigns: boolean;
    readonly loginMethod?: string;
}

export interface AuthContextEventMap {
    login: Zeta.ZetaEvent & { user: UserContext, passwordStatus: PasswordStatus };
    logout: Zeta.ZetaEvent;
    sessionExpired: Zeta.ZetaAsyncHandleableEvent;
}

export class AuthContext {
    readonly isLoggedIn: boolean = false;
    readonly currentUser: UserContext | undefined;
    readonly passwordStatus: PasswordStatus = PasswordStatus.none;
    name: any = {};
    role: any = {};
    lang: any = {
        settings: {
            language: ''
        }
    };
    reminder = { blockings: [] as any };
    ssoAppId: string = "";
    targetApp: string = "";
    loginMethod: string = "";

    constructor(private readonly app: typeof import("./app").app) {
        app.beforeInit(async () => {
            // Clear stale cookies before reading or requesting an authentication token.
            this.clearNonEssentialCookies();
            if (!isViewMatched(ResetPassword)) {
                sessionStorage.removeItem("postmessage-flag");
                let token: any = await this.getInitialToken();

                if (!token && window.opener) {
                    sessionStorage.setItem("postmessage-flag", "true");
                    const eacToken = await this.transferIHSToken();
                    token = eacToken;
                }

                if (token) {
                    window.name = '';
                    Bus.addListener("initInfo", () => {
                        this.loginSilent(token!);
                    });
                    cookie(STORAGE_KEY_TOKEN).set(token);
                    await this.validateToken(token);
                    await this.loginSilent(token);
                }
            }
        });
    }

    on<E extends keyof AuthContextEventMap>(event: E, handler: Zeta.ZetaEventHandler<E, AuthContextEventMap, AuthContext>) {
        return emitter.add(this, event, handler);
    }

    get accessToken() {
        return authHeader?.authorization.slice(7) || null;
    }

    async login() {
        let token = await api.login();
        await this.loginSilent(token);
    }

    async logout() {
        this.logoutSilent();
    }

    async dismissPasswordStatus() {
        assign(this as AuthContext, {
            passwordStatus: PasswordStatus.none
        });
    }

    private async validateToken(token: string) {
        try {
            sessionStorage.removeItem("error_api_urls");
            authHeader = { authorization: 'Bearer ' + token };
            newAuthHeader = { authorization: 'Bearer ' + token };
            let result = await api.validateToken();
            if (!result) {
                throw errorWithCode(ErrorCode.sessionExpired);
            }
            authHeader['x-AIAHK-Agent-ID'] = result.agentId;
        } catch (err: any) {
            if (err?.code !== ErrorCode.sessionExpired) {
                await this.app.handleError(err);
            }
            if (config.enableLoginPage || localStorage.getItem(STORAGE_KEY_REDIRECT) === 'no') {
                this.logoutSilent();
            } else {
                // set redirect flag to "no" to avoid redirection loop
                // when old eAC redirects back here with token but failed to successfully login here
                localStorage.setItem(STORAGE_KEY_REDIRECT, 'no');
                window.location.replace(combinePath(CLASSIC_EAC_URL, 'redirecCloudEac.do'));
            }
        }
    }

    private async loginSilent(token: string) {
        try {
            await resolveAll([api.getNameInfo(), api.getRoleInfo(), api.getLangInfo(), api.getReminderInfo()]);
            removeErrorApi("initInfo");
            const info: any = { ...this.name, ...this.role, ...this.lang, ...this.reminder };
            this.clearNonEssentialCookies();
            this.setCurrentUser(info, await api.getPasswordStatus());
            this.monitorSession(token);
            localStorage.removeItem(STORAGE_KEY_REDIRECT);
        } catch (err: any) {
            if (err?.code !== ErrorCode.sessionExpired) {
                await this.app.handleError(err);
            }
        }
    }

    /**
 * Clears browser-accessible cookies while preserving the EAC token and the
 * device identifier. HttpOnly and cross-domain cookies must be expired by
 * their issuing server.
 */
    private clearNonEssentialCookies() {
        const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0';
        const cookieNames = document.cookie
            .split(';')
            .map(v => v.trim().split('=', 1)[0])
            .filter(name => name && !PRESERVED_COOKIE_NAMES.has(name));

        cookieNames.forEach(name => {
            document.cookie = `${name}=; ${expired}; path=/`;
        });
    }

    logoutSilent(sendExpired?: boolean) {
        cookie(STORAGE_KEY_TOKEN).delete();
        localStorage.removeItem(STORAGE_KEY_REDIRECT);
        authHeader = undefined;
        if (sendExpired) {
            always(emitter.emit('sessionExpired', this), () => {
                this.logoutSilent();
            });
        } else if (config.enableLoginPage) {
            this.setCurrentUser(undefined);
        } else {
            window.location.replace(combinePath(CLASSIC_EAC_URL, 'logout.do'));
        }
    }

    private async setCurrentUser(info: UserInfo | undefined, passwordStatus: PasswordStatus = PasswordStatus.none) {
        const app = this.app;
        assign(this as AuthContext, {
            isLoggedIn: !!info,
            passwordStatus: passwordStatus,
            currentUser: info && Object.freeze(<UserContext>{
                ...info,
                loginMethod: this.loginMethod,
                get canViewMyPerformancePage() {
                    return app.canViewMyPerformancePage;
                },
                get canViewCampaigns() {
                    return app.canViewCampaigns;
                }
            })
        });
        emitter.emit(info ? 'login' : 'logout', this, { user: info, passwordStatus: passwordStatus });
    }

    private monitorSession(token: string) {
        let idleTimeout = parseDuration(config.idleTimeout);
        let lastInteract = Date.now();
        let expiryTime = parseJWT(token)?.exp * 1000;
        let unbind = combineFn(
            this.on('logout', () => {
                unbind();
            }),
            api.on('error', e => {
                if (isErrorWithCode(e.error, ErrorCode.sessionExpired)) {
                    this.logoutSilent(true);
                }
            }),
            bind(window, 'pageshow', async (e) => {
                if (e.persisted && !await api.validateToken()) {
                    this.logoutSilent(true);
                }
            }),
            bind(window, 'keydown mousedown touchstart wheel', () => {
                lastInteract = Date.now();
                localStorage.setItem(STORAGE_KEY_INTERACT, String(lastInteract));
            }),
            setIntervalSafe(async () => {
                lastInteract = +(localStorage.getItem(STORAGE_KEY_INTERACT) || '') || lastInteract;
                if (Date.now() - lastInteract > idleTimeout) {
                    this.logoutSilent(true);
                    unbind();
                } else if (expiryTime - Date.now() < 120000) {
                    try {
                        // 调用 refreshForgerockToken
                        const forgerockResult = await api.refreshFRStatus();
                        if (forgerockResult.result === 'fail') {
                            this.logoutSilent(true);
                            unbind();
                            return;
                        }
                        // 刷新 token
                        const token1 = await api.refreshToken();
                        expiryTime = parseJWT(token1)?.exp * 1000;
                        authHeader = {
                            ...authHeader,
                            authorization: 'Bearer ' + token1
                        };
                        newAuthHeader = { authorization: 'Bearer ' + token1 };
                        cookie(STORAGE_KEY_TOKEN).set(token1);
                    } catch {
                        this.logoutSilent(true);
                        unbind();
                    }
                }
            }, 10000)
        );
        localStorage.setItem(STORAGE_KEY_INTERACT, String(lastInteract));
    }

    private transferIHSToken(): Promise<string> {
        return new Promise((resolve, reject) => {
            const processMessage = (event: any) => {
                // if (event.origin !== 'https://ihs.example.com') return;

                const data = event.data;
                if (!data || typeof data !== 'object') return;

                if (data.type === 'params') {
                    const { targetAppId, ihsToken } = data;
                    console.log('EAC 收到的参数：', { targetAppId, ihsToken });
                    this.targetApp = targetAppId;
                    if (window.opener) {
                        window.opener.postMessage({ type: 'ack' }, IHS_HOST);
                    }

                    // 如果有 ihsToken，则先调用 Exchange API 获取 EAC token
                    if (ihsToken) {
                        (async () => {
                            authHeader = { authorization: 'Bearer ' + ihsToken };
                            const eacToken = await api.exchangeToken("eac");
                            console.log('已换取并缓存 EAC token。');
                            resolve(eacToken);
                            window.removeEventListener('message', processMessage);
                            sessionStorage.removeItem("postmessage-flag");
                        })();
                    }
                }
            };

            window.addEventListener('message', processMessage, false);

            if (window.opener) {
                window.opener.postMessage({ type: 'ready' }, IHS_HOST);
                console.log('已向 ihs 页面发送 ready 消息。');
            } else {
                window.removeEventListener('message', processMessage);
                sessionStorage.removeItem("postmessage-flag");
                reject(new Error('Token not found in postmessage'));
            }
        });
    }

    private async getInitialToken() {
        // 初始化ssoStatus，从sessionStorage中获取
        if (sessionStorage.getItem('ssoStatus')) {
            ssoStatus = sessionStorage.getItem('ssoStatus') === 'true';
        }

        if (window.name) {
            const nameParts = window.name.split('&');
            this.sendAppIdtoAA(nameParts);
            // 第一个部分是token，不需要 token= 前缀
            const token = nameParts[0];
            if (token) {
                const sso = nameParts.find(part => part.startsWith('sso='));
                this.handleSsoStatus(sso)
                return token;
            }
        }
        if (/#token=([^&]+)/.test(window.location.hash)) {
            const token = RegExp.$1;
            const hashParts = window.location.hash.split('&');
            this.sendAppIdtoAA(hashParts);
            const sso = hashParts.find(part => part.startsWith('sso='));
            this.handleSsoStatus(sso)
            window.location.hash = '';
            return token;
        }
        if (/\?token=([^&]+)/.test(window.location.search)) {
            const token = RegExp.$1;
            const searchParts = window.location.search.split('&');
            this.sendAppIdtoAA(searchParts);
            const sso = searchParts.find(part => part.startsWith('sso='));
            this.handleSsoStatus(sso)
            return token;
        }
        return cookie(STORAGE_KEY_TOKEN).get();
    }
    handleSsoStatus(sso: string | undefined) {
        if (sso && sso.endsWith('=Y')) {
            ssoStatus = true;
            sessionStorage.setItem('ssoStatus', 'true');
        }
    }
    sendAppIdtoAA(part: string[]) {
        const appPart = part.find(part => part.startsWith('appid'));
        const loginPart = part.find(part => part.startsWith('loginType'));
        const appParams = new URLSearchParams(appPart);
        const loginParams = new URLSearchParams(loginPart);
        const appId = appParams.get('appid');
        const loginMethod = loginParams.get('loginType');
        if (appId) {
            this.ssoAppId = appId;
            this.loginMethod = `app:${appId}`;
            sendAnalytic('onApptoEAC', { app: `app:${appId}` });
        } else {
            this.loginMethod = `website:${loginMethod}`;
            sendAnalytic('onApptoEAC', { app: `website:${loginMethod}` });
        }
    }
}
